import { Star, Facebook, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import ReviewCard from "@/components/ReviewCard";
import { REVIEWS, REVIEWS_RATING, BUSINESS } from "@/content/siteContent";

const Stars = () => (
  <div className="flex gap-1 text-gold" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4" fill="currentColor" />)}
  </div>
);

export default function Reviews() {
  return (
    <>
      <Seo
        title="Customer Reviews | Home Team Moving Co."
        siteName="Home Team Moving Co."
        description="See what Southern California families and businesses say about Home Team Moving Co. — veteran-owned moving, hauling, and cleanouts."
      />
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <FadeUp>
            <p className="eyebrow mb-4">Reviews</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
              Earned, <span className="text-gold">not asked for.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-12 flex flex-wrap items-center gap-8 border border-white/10 bg-navy-900/50 p-8">
            <div>
              <p className="font-display text-6xl font-extrabold leading-none text-gold">{REVIEWS_RATING}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.25em] text-slate-400">Average rating</p>
            </div>
            <div className="hidden h-16 w-px bg-white/10 sm:block" />
            <div className="flex-1">
              <Stars />
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-300">
                Reviews below come from our Facebook community. Every job is a chance to earn the next one — that's how a veteran-owned business grows.
              </p>
            </div>
            <a
              href={BUSINESS.facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-reviews-link"
              className="inline-flex items-center gap-2 border border-gold/50 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-950"
            >
              <Facebook className="h-4 w-4" /> See all on Facebook
            </a>
          </FadeUp>
        </div>
      </section>
      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <div data-testid="reviews-section" className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {REVIEWS.map((r, i) => (
              <FadeUp key={r.name} delay={i * 0.1} className="h-full">
                <ReviewCard review={r} />
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-16 text-center">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
              Ready to write your own five-star story?
            </h2>
            <Link
              to="/quote"
              data-testid="reviews-quote-cta"
              className="mt-7 inline-flex items-center gap-2 bg-gold px-9 py-4 font-display text-base font-bold uppercase tracking-[0.2em] text-navy-950 transition-colors duration-300 hover:bg-gold-light"
            >
              Request a quote <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
